// import { Route, Link } from 'react-router-dom';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Route, Switch, withRouter } from 'react-router';
import { hot } from 'react-hot-loader';
import ReactLoading from 'react-loading';
import { get } from 'lodash-es';
import axios from 'axios';

import { bbSubDomain, cfPath } from '../../utilities/UrlPaths';
import { updateUser, updateUserTransformer } from '../../modules/user';
import { updateHouse, updateHouseTransformer } from '../../modules/house';
import { updatePolicy } from '../../modules/policy';
import Header from '../header';
import Footer from '../footer';
import NotFound from '../NotFound';
import Auction from '../Auction';
import Snapshots from '../Auction/Snapshots/Snapshots';
import Customer from '../Customer';
import DiyContainer from '../DIY/DiyContainer';
import BinContainer from '../DIY/BIN/BinContainer';
import AddLot from '../DIY/AddLot/AddLot';
import Locations from '../Locations/Locations';
import CampaignReporting from '../CampaignReporting';
import RealizePriceContainer from '../DIY/RealizePriceContainer';
import PasswordReset from '../PasswordReset';
import Payments from '../Payments';
import PLSettings from '../Settings/PLSettings';
import Invoices, {
  CreateInvoices,
  EditInvoices,
  InvoiceAutoCharge,
  InvoiceGroupDetails,
  InvoiceGroupPreview,
  InvoiceTemplates,
  SentInvoices
} from '../Invoices';
import SalesTaxReports from '../SalesTaxReports';
import '../../index.scss';
import BulkBidding from '../BulkBidding/BulkBidding';
import bbApi from '../../modules/bulkBidding/service';
import Broadcast from '../Broadcast';
import Disputes from '../Customer/Disputes';
import ConfirmDisputes from '../Customer/Disputes/ConfirmDisputes';
import SingleDisputes from '../Customer/Disputes/SingleDisputes';
import SessionResults from '../Reports/SessionResults';
import SaleResults from '../Reports/SalesResults';
import OpenDisputes from '../Reports/OpenDisputes';
import DefaultFeedbackPage from '../Reports/Feedback';
import UpdateNotification from './UpdateNotification';
import { construct } from 'core-js/fn/reflect';

/**
 * (dyouberg) TODO: The chunking needs to be investigated.  This was breaking when
 * deployed to stage.  To re-enable replace Auction with AuctionASync in the <Route> below
 *
 * If we want to retry this - we need to re-add the "loadable-components" package
 */
// const AuctionAsync = loadable(
//   () => import(/* webpackChunkName: "Auction" */ '../Auction/index.js'),
//   {
//     LoadingComponent: () => <div>Loading...</div>,
//     modules: ['Auction']
//   }
// );

// bypassing login check & header
const isPwResetPage = window.location.pathname.indexOf('/password-reset') > -1;
const showHeader = !isPwResetPage;

class App extends Component {
  constructor(props) {
    super(props);
    this.state = { showUpdate: false, intervalID: null };
  }

  versionCheck = () => {
    fetch('/ar/version.json', { cache: 'no-store' })
      .then(res => res.json())
      .then(data => {
        const storedVersion = localStorage.getItem('appVersion');
        if (storedVersion !== data.version) {
          console.log('New version detected, reloading...');
          this.setState({ showUpdate: true });
          localStorage.setItem('appVersion', data.version);
        }
      })
      .catch(() => console.warn('Failed to fetch version.json'));
  };

  componentDidMount = async () => {
    // eslint-disable-next-line no-shadow
    const { updateUser, updateHouse } = this.props;

    setInterval(this.versionCheck, 300000);

    if (!isPwResetPage) {
      try {
        const userResponse = await axios('/app/house/user', { withCredentials: true });
        const data = get(userResponse, 'data');

        // Do the real super user API call...
        const houseRef = get(data, 'data.user.houseInfo.houseRef', '');
        const houseUserName = get(data, 'data.user.houseInfo.houseUserName', '');
        const houseID = get(data, 'data.user.houseInfo.houseID');

        try {
          // fetching policy data for Invaluable Shipping
          if (houseID) {
            const policyResponse = await axios('/api/shipping/policy', {
              headers: {
                'Content-Type': 'application/json',
                'x-invaluable-shipping-account-id': `${houseID}`,
                'x-invaluable-shipping-account-domain': 'AUCTIONHOUSE'
              }
            });

            const policyData = policyResponse?.data.data.policy;
            this.props.updatePolicy(policyData ? policyData : null);
          }
        }
        catch (err) {
          console.warn('Failed to fetch policy data', err);
          // TODO: handle error so it's not a false negative when checking if shipping is enabled
        }

        const userRef = get(data, 'data.user.userRef', '');

        const [superUserResponse] = await Promise.all([
          axios(`/app/house/${houseRef}/superuser/${userRef}`)
        ]);

        // The API returns 404 for stealth houses, so in that case houseConfig would be blank
        let houseConfig = {};
        try {
          const res = await bbApi.get(`auctions/auction-houses/username/${houseUserName}`);
          houseConfig = res.data;
        } catch (err) {
          console.warn(`Failed to fetch config for house: ${houseUserName}`, err);
        }

        const isSuperUser = get(superUserResponse, 'data.data.superUser');

        updateUser(updateUserTransformer(data.data, isSuperUser, houseConfig));
        updateHouse(updateHouseTransformer(data, houseConfig));
        this.setCookieData(data);
      } catch {
        // Could not retrieve user data, they were probably not logged in
        window.location.href = `${cfPath()}/index.cfm`;
      }
    }
  };

  componentWillUnmount = () => {
    clearInterval(this.intervalID);
    this.intervalID = null;
  }

  setCookieData = ({ data }) => {
    const houseID = get(data, 'user.houseInfo.houseID', '');
    const houseRef = get(data, 'user.houseInfo.houseRef', '');
    const userRef = get(data, 'user.userRef', '');
    const userID = get(data, 'user.userID', '');
    const isHouseOnlineOnly = get(data, 'user.houseInfo.isOnlineOnly', true);

    document.cookie = `houseID=${houseID};path=/`;
    document.cookie = `houseRef=${houseRef};path=/`;
    document.cookie = `userRef=${userRef};path=/`;
    document.cookie = `userID=${userID};path=/`;
    document.cookie = `ISONLINEONLY=${isHouseOnlineOnly};path=/`;
  };

  hasCustomerAccess = (user, component) => {
    const userRef = get(user, 'userRef', '');
    const accessCustomer = get(user, 'accessCustomer', '');
    if (!userRef) return <ReactLoading className="loading-spinner" color="black" type="spin" />;
    if (!accessCustomer) {
      const hostServer = `https://${bbSubDomain()}.invaluableauctions.com`;
      window.location.href = `${hostServer}/?p=1`;
      return;
    }
    return <Route component={component} />;
  };

  render() {
    const { user, house } = this.props;
    const { showUpdate } = this.state;
    return (

      <div>
        {showHeader
        && <Header user={user} house={house} />}

        <div className="content-body">
          <Switch>
            <Route exact path="/invoices" component={Invoices} />
            <Route
              exact
              path="/bulk-bidding/:catalogRef"
              render={() => (get(user, 'accessBulkbiding') ? (
                <Route component={BulkBidding} />
              ) : (
                <></>
              ))}
            />
            <Route exact path="/invoices/EditInvoices/:invoiceGroupID" component={EditInvoices} />
            <Route exact path="/invoices/SentInvoices/:invoiceGroupID/:invoiceIDList" component={SentInvoices} />
            <Route exact path="/invoices/ExistingInvoices" component={Invoices} />
            <Route exact path="/invoices/InvoiceTemplates" component={InvoiceTemplates} />
            <Route exact path="/invoices/InvoiceTemplates/:settingsID" component={InvoiceTemplates} />
            <Route exact path="/invoices/InvoiceGroupDetails/:invoiceGroupID" component={InvoiceGroupDetails} />
            <Route exact path="/invoices/InvoiceAutoCharge/:invoiceGroupID/invoice/:invoiceIDList" component={InvoiceAutoCharge} />
            <Route exact path="/invoices/InvoiceGroupPreview/:invoiceGroupID/invoice/:invoiceIDList" component={InvoiceGroupPreview} />
            <Route exact path="/invoices/CreateNewInvoices/:invoiceGroupID" component={CreateInvoices} />
            <Route exact path="/reports/salesTaxReports" component={SalesTaxReports} />
            <Route exact path="/my-auctions/snapshots" component={Snapshots} />
            <Route exact path="/reports/sessionResults" component={SessionResults} />
            <Route
              exact
              path="/reports/sessionResults/viewSessionResults"
              component={SaleResults}
            />
            <Route exact path="/reports/openDisputes" component={OpenDisputes} />
            <Route exact path="/customers/feedback/default" component={DefaultFeedbackPage} />
            <Route
              exact
              path="/my-auctions/:type"
              render={() => (get(house, 'houseRef') ? (
                <Route component={Auction} />
              ) : (
                <ReactLoading className="loading-spinner" color="black" type="spin" />
              ))}
            />
            <Route exact path="/settings/locations" component={Locations} />
            {/* This add/edit lot route needs to be above the DIY routes */}
            <Route
              exact
              path="/session/:type/:groupID/:catalogID/lot/:lotNumber/:goToPage"
              render={() => (get(house, 'houseID') ? (
                <Route component={AddLot} />
              ) : (
                <ReactLoading className="loading-spinner" color="black" type="spin" />
              ))}
            />
            <Route
              exact
              path="/session/:type/:groupID/:catalogID/lot/:lotNumber"
              render={() => (get(house, 'houseID') ? (
                <Route component={AddLot} />
              ) : (
                <ReactLoading className="loading-spinner" color="black" type="spin" />
              ))}
            />
            <Route
              path="/session/:type/:groupID/:catalogID/:step/:goToPage"
              render={props => {
                if (get(house, 'houseRef')) {
                  if (props.location.pathname.toLowerCase().includes('bin')) {
                    return <Route component={BinContainer} />;
                  }
                  return <Route component={DiyContainer} />;
                }
                return <ReactLoading className="loading-spinner" color="black" type="spin" />;
              }}
            />
            <Route
              path="/session/:type/:groupID/:catalogID/:step"
              render={props => {
                if (get(house, 'houseRef')) {
                  if (props.location.pathname.toLowerCase().includes('bin')) {
                    return <Route component={BinContainer} />;
                  }
                  return <Route component={DiyContainer} />;
                }
                return <ReactLoading className="loading-spinner" color="black" type="spin" />;
              }}
            />
            <Route
              exact
              path="/pricesrealized/:catalogID"
              render={() => (get(house, 'houseRef') ? (
                <Route component={RealizePriceContainer} />
              ) : (
                <ReactLoading className="loading-spinner" color="black" type="spin" />
              ))}
            />
            <Route
              exact
              path="/your-customers"
              render={() => (get(house, 'houseRef') ? (
                <Route component={Customer} />
              ) : (
                <ReactLoading className="loading-spinner" color="black" type="spin" />
              ))}
            />
            <Route
              exact
              path="/campaign-reporting"
              render={() => (get(house, 'houseRef') ? (
                <Route component={CampaignReporting} />
              ) : (
                <ReactLoading className="loading-spinner" color="black" type="spin" />
              ))}
            />
            <Route exact path="/broadcast/:eventId" component={Broadcast} />
            <Route exact path="/password-reset" component={PasswordReset} />
            <Route exact path="/payments" component={Payments} />
            <Route exact path="/payments/:action" component={Payments} />
            <Route exact path="/settings/plSettings" component={PLSettings} />
            <Route
              exact
              path="/customers/viewDisputes"
              render={() => this.hasCustomerAccess(user, Disputes)}
            />
            <Route
              exact
              path="/customers/confirmDisputeAction"
              render={() => this.hasCustomerAccess(user, ConfirmDisputes)}
            />
            <Route
              exact
              path="/customers/dispute/:disputeRef"
              render={() => this.hasCustomerAccess(user, SingleDisputes)}
            />

            <Route component={NotFound} />
          </Switch>
        </div>
        <UpdateNotification
          isOpen={showUpdate}
          closeUpdateModal={() => this.setState({ showUpdate: !showUpdate })}
        />
        <Footer />
      </div>
    );
  }
}

const mapStateToProps = ({ user, house, policy }) => ({
  user,
  house,
  policy
});

const mapDispatchToProps = {
  updateUser,
  updateHouse,
  updatePolicy
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(hot(module)(App)));
